/* eslint-env browser */
// Booking details (approved board W2_Portal_BookingDetails): one page. A
// summary on top (reference, status, booking type, the fare as Fixed price /
// Estimate / Fare, pickup → drop-off, customer and driver), the live map slot
// beside the trip timeline (Addis Ababa time, 12-hour clock), then the Trip,
// Payment and Customer and driver cards. Every field and action of the old
// page is kept: receipt, Adjust fare, the distance tracking card and the list
// of customers on a shared trip.
//
// Data: the same one-booking listener as before (plus the first paint from
// the bookings already in Redux). The agent's name comes from the staff list
// already loaded (staticusers); nothing else is read.
//
// Call-centre feedback (29 Oct 2026): every phone number shown here has a
// copy button that copies it without spaces (+251964540448), ready for
// MicroSIP; the reference and the IDs keep theirs (the shared CopyButton). A
// number hidden in demo mode has no copy button.
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Avatar,
  Rating,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import HailOutlinedIcon from "@mui/icons-material/HailOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import { getLangKey } from "common/src/other/getLangKey";
import { updateBooking } from 'common/src/actions/bookinglistactions';
import { FirebaseContext } from 'common';
import { onValue, runTransaction } from 'firebase/database';
import ClickableUserLink from '../components/ClickableUserLink';
import LiveTrackingMap, { LIVE_TRACKING_STATUSES } from '../components/LiveTrackingMap';
import { goBackOr, usePageState } from '../components/modern/pageState';
import { CopyButton } from '../components/modern';
import { getBookingType, getBookingTypeLabel, generateReceiptHtml } from "../common/sharedFunctions";
import { tokens as c, tones } from "../theme";
import { showToast, InlineMessage, SkeletonRows } from "../components/feedback";
import { printDocument, formatAddisTime, formatAddisDateTime, FONT_STACK } from "../common/exportUtils";
import {
  BookingStatusChip,
  FareTag,
  AnomalyMark,
  analyseFare,
  analysisLines,
  anomalyMarkText,
  fareInfoOf,
  formatMoney,
  formatPhoneDisplay,
  calmCaps,
  toMs,
} from "./BookingHistory";

// Monday-00:00 in Addis Ababa (UTC+3, no DST) for the given epoch-ms,
// returned as true UTC ms. Mirrors getAddisWeekStartMs in
// functions/common/index.js so the web admin computes the exact same
// week boundary the Cloud Function used when it accumulated the driver's
// weeklyTripRevenue — letting us tell whether an adjusted trip still
// belongs to the week the driver record is currently tracking.
const ADDIS_OFFSET_MS = 3 * 60 * 60 * 1000;
const getAddisWeekStartMs = (nowMs) => {
  const addisNow = new Date(nowMs + ADDIS_OFFSET_MS);
  const day = addisNow.getUTCDay();
  const daysSinceMon = (day + 6) % 7;
  const addisMidnightMon = Date.UTC(
    addisNow.getUTCFullYear(),
    addisNow.getUTCMonth(),
    addisNow.getUTCDate() - daysSinceMon
  );
  return addisMidnightMon - ADDIS_OFFSET_MS;
};

// Booking timestamps arrive as either epoch-ms (number or all-digit
// string) or an ISO-8601 string depending on the creation path
// (street/meter/shared trips differ). Normalize to epoch-ms; return
// null when it can't be resolved.
const parseBookingTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const str = String(value).trim();
  if (/^\d+$/.test(str)) return Number(str);
  const ms = new Date(str).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Sun 25 Oct" (year added when it is not this year), Addis calendar.
const addisDayLabel = (value) => {
  const ms = toMs(value);
  if (ms === null) return '';
  const a = new Date(ms + ADDIS_OFFSET_MS);
  const now = new Date(Date.now() + ADDIS_OFFSET_MS);
  const year = a.getUTCFullYear() !== now.getUTCFullYear() ? ` ${a.getUTCFullYear()}` : '';
  return `${WEEKDAYS[a.getUTCDay()]} ${a.getUTCDate()} ${MONTHS[a.getUTCMonth()]}${year}`;
};

// A clock time saved by a phone as "14:5:9" (device time, 24-hour) shown as
// "2:05 pm". Only for old bookings without the millisecond stamps.
const deviceClockTo12h = (value) => {
  const m = /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/.exec(String(value || '').trim());
  if (!m) return '';
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return '';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mi < 10 ? `0${mi}` : mi} ${h < 12 ? 'am' : 'pm'}`;
};

// A start/end time: the millisecond stamp in Addis time, else the old
// device string turned into 12-hour time, else ''.
const tripClock = (ms, deviceString) => {
  const v = toMs(ms);
  if (v !== null) return formatAddisTime(v);
  if (deviceString && /^\d{4}-\d{2}-\d{2}T/.test(String(deviceString))) return formatAddisTime(deviceString);
  return deviceClockTo12h(deviceString);
};

const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

const TYPE_ICON = {
  app: PhoneIphoneOutlinedIcon,
  call_center: HeadsetMicOutlinedIcon,
  admin: AdminPanelSettingsOutlinedIcon,
  street_pickup: HailOutlinedIcon,
  donkey_meter: SpeedOutlinedIcon,
  offline: CloudOffOutlinedIcon,
};

// One label / value row of a card.
function InfoRow({ label, children, alignTop }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(96px, 32%) minmax(0, 1fr)',
        columnGap: 1.5,
        alignItems: alignTop ? 'start' : 'center',
        py: 1.1,
        borderTop: `1px solid ${c.OUTLINE}`,
        '&:first-of-type': { borderTop: 0 },
      }}
    >
      <Typography component="dt" sx={{ fontSize: 13.5, color: c.INK_2, lineHeight: 1.45 }}>
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0, fontSize: 14, color: c.INK, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
        {children}
      </Box>
    </Box>
  );
}

// A card: an icon on a tinted circle, a title, then label / value rows.
function DetailCard({ icon, hue, title, action, children, sx }) {
  const tone = hue || tones.accent;
  return (
    <Paper component="section" aria-label={typeof title === 'string' ? title : undefined} sx={{ p: 2, minWidth: 0, ...(sx || {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: tone.bg, color: tone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', '& .MuiSvgIcon-root': { fontSize: 19 } }}
        >
          {icon}
        </Box>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {action || null}
      </Box>
      <Box component="dl" sx={{ m: 0 }}>
        {children}
      </Box>
    </Paper>
  );
}

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState([]);
  const settings = useSelector((state) => state.settingsdata.settings);
  const cartypes = useSelector((state) => state.cartypes);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const role = useSelector(state => state.auth.profile.usertype);
  const auth = useSelector(state => state.auth.profile);
  const { state } = useLocation();
  const firebase = useContext(FirebaseContext);

  // State for editing trip cost
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newTripCost, setNewTripCost] = useState('');
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!firebase || !id) return undefined;

    // Fast initial render from Redux if the booking is already loaded —
    // avoids a brief blank state while the live listener fires its first
    // callback. After this, the listener is the source of truth.
    const reduxBookings = bookinglistdata.bookings;
    if (reduxBookings) {
      const initial =
        reduxBookings.find((item) => item.id === id.toString())
        || reduxBookings.find((item) => item.reference === id);
      if (initial) setData(initial);
    }

    // Live subscription to this single booking. Each status flip from
    // the mobile driver / Cloud Function settlement / another admin
    // streams in as a small payload (~5 KB) over the existing wss
    // connection — no polling, no manual refresh. Detaches automatically
    // on unmount or when the viewed booking id changes.
    const dbRef = firebase.singleBookingRef(id);
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const raw = snapshot.val();
        if (!raw) {
          navigate('/404');
          setData([]);
          return;
        }
        const booking = { ...raw };
        booking.id = id;
        booking.pickupAddress = booking.pickup ? booking.pickup.add : '';
        booking.dropAddress = booking.drop ? booking.drop.add : '';
        booking.discount = booking.discount || 0;
        booking.cashPaymentAmount = booking.cashPaymentAmount || 0;
        booking.cardPaymentAmount = booking.cardPaymentAmount || 0;
        // When it was booked (the timeline), before bookingDate becomes the
        // trip date as everywhere else on the portal.
        booking.bookedAt = raw.bookingDate;
        booking.bookingDate = booking.tripdate || booking.bookingDate;
        setData(booking);
      },
      (error) => {
        console.error('Error subscribing to booking:', error);
        navigate('/404');
        setData([]);
      }
    );

    return () => {
      try { unsubscribe(); } catch (_) { /* already detached */ }
    };
    // bookinglistdata.bookings deliberately excluded from deps — it's
    // only used for the initial fast paint; subsequent updates come
    // from the onValue listener, which would otherwise be torn down
    // and re-attached every time the booking list refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, firebase]);

  // "Trip started 9 min ago" stays true while the trip is live.
  const live = !!(data && LIVE_TRACKING_STATUSES.indexOf(data.status) >= 0);
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, [live]);

  // The new fare's split, or why it cannot be worked out. Pure: used for the
  // preview while typing and again on save.
  const computeAdjustedAmounts = (newCost) => {
    const tripCost = parseFloat(newCost);
    if (isNaN(tripCost) || tripCost <= 0) {
      return { ok: false, reason: 'invalid' };
    }

    // Flat-daily drivers pay no per-trip commission — their commission is
    // the fixed daily rate the settlement sweeper deducts from topUpBalance.
    // Recomputing a per-trip convenience fee here would claw the waived
    // commission back out of the driver's wallet on every admin fare
    // correction, so the full adjusted fare stays with the driver and all
    // commission fields remain 0. Short-circuit before the car-type lookup:
    // none of its commission data applies to a flat booking.
    if (data.commission_mode === 'flat_daily' || data.flat_rate_driver === true) {
      const roundedCost = parseFloat(tripCost.toFixed(settings.decimal));
      // Flat plan: the driver keeps the FULL fare — no platform commission
      // and no fleet cut (fleet admins earn nothing from plan drivers).
      return {
        ok: true,
        values: {
          trip_cost: roundedCost,
          convenience_fees: 0,
          driver_share: roundedCost,
          fleetCommission: 0
        },
      };
    }

    // Get car type details for commission calculation
    let carTypeDetails = null;
    if (cartypes.cars) {
      carTypeDetails = cartypes.cars.find(car => car.name === data.carType);
    }

    if (!carTypeDetails) {
      return { ok: false, reason: 'no_car_type' };
    }

    // Calculate convenience fee (commission)
    let convenienceFee = 0;
    if (carTypeDetails.convenience_fee_type === 'flat') {
      convenienceFee = parseFloat(carTypeDetails.convenience_fees);
    } else {
      convenienceFee = (tripCost * parseFloat(carTypeDetails.convenience_fees) / 100);
    }
    convenienceFee = parseFloat(convenienceFee.toFixed(settings.decimal));

    // Calculate fleet commission if applicable
    let fleetCommission = 0;
    if (data.fleetadmin && carTypeDetails.fleet_admin_fee) {
      const driverShareBeforeFleet = tripCost - convenienceFee;
      fleetCommission = parseFloat(((driverShareBeforeFleet * parseFloat(carTypeDetails.fleet_admin_fee) / 100)).toFixed(settings.decimal));
    }

    // Calculate driver share
    const driverShare = parseFloat((tripCost - convenienceFee - fleetCommission).toFixed(settings.decimal));

    return {
      ok: true,
      values: {
        trip_cost: parseFloat(tripCost.toFixed(settings.decimal)),
        convenience_fees: convenienceFee,
        driver_share: driverShare,
        fleetCommission: fleetCommission
      },
    };
  };

  const adjustProblemText = (reason) =>
    reason === 'no_car_type'
      ? t('booking_page_no_car_type', { defaultValue: 'The car type of this booking was not found, so the fare cannot be worked out again.' })
      : t('booking_page_invalid_fare', { defaultValue: 'Enter a fare above 0.' });

  // Function to handle trip cost update
  const handleTripCostUpdate = async () => {
    const result = computeAdjustedAmounts(newTripCost);
    if (!result.ok) {
      showToast.error(adjustProblemText(result.reason));
      return;
    }
    const recalculated = result.values;
    setSaving(true);

    // Create audit log entry
    const auditLog = {
      timestamp: new Date().getTime(),
      action: 'TRIP_COST_ADJUSTMENT',
      performedBy: {
        uid: auth.uid,
        email: auth.email,
        name: `${auth.firstName} ${auth.lastName}`,
        role: role
      },
      bookingId: data.id,
      bookingReference: data.reference,
      oldValues: {
        trip_cost: parseFloat(data.trip_cost),
        driver_share: parseFloat(data.driver_share),
        convenience_fees: parseFloat(data.convenience_fees),
        customer_paid: parseFloat(data.customer_paid || 0),
        fleetCommission: parseFloat(data.fleetCommission || 0)
      },
      newValues: {
        trip_cost: recalculated.trip_cost,
        driver_share: recalculated.driver_share,
        convenience_fees: recalculated.convenience_fees,
        customer_paid: recalculated.trip_cost,
        fleetCommission: recalculated.fleetCommission
      },
      paymentMode: data.payment_mode || 'cash',
      reason: 'Manual correction via admin panel'
    };

    // Create updated booking object (bookedAt is display-only, not saved)
    const { bookedAt, ...savedFields } = data;
    const updatedBooking = {
      ...savedFields,
      trip_cost: recalculated.trip_cost,
      convenience_fees: recalculated.convenience_fees,
      driver_share: recalculated.driver_share,
      // Update payment amounts based on payment mode
      customer_paid: recalculated.trip_cost,
      manual_cost_adjustment: true,
      manual_cost_adjustment_date: new Date().getTime(),
      manual_cost_adjustment_by: auth.uid,
      manual_cost_adjustment_by_name: `${auth.firstName} ${auth.lastName}`,
      manual_cost_adjustment_by_email: auth.email,
      manual_cost_adjustment_audit: auditLog,
    };

    // Only set fleetCommission if it exists (booking has fleet admin)
    if (data.fleetadmin && recalculated.fleetCommission !== undefined) {
      updatedBooking.fleetCommission = recalculated.fleetCommission;
    }

    // Update payment amounts based on payment mode
    const payment_mode = data.payment_mode || 'cash';
    if (payment_mode === 'cash') {
      updatedBooking.cashPaymentAmount = recalculated.trip_cost;
      updatedBooking.cardPaymentAmount = 0;
      updatedBooking.usedWalletMoney = 0;
    } else if (payment_mode === 'wallet') {
      updatedBooking.usedWalletMoney = recalculated.trip_cost;
      updatedBooking.cashPaymentAmount = 0;
      updatedBooking.cardPaymentAmount = 0;
    } else if (payment_mode === 'card') {
      updatedBooking.cardPaymentAmount = recalculated.trip_cost;
      updatedBooking.cashPaymentAmount = 0;
      updatedBooking.usedWalletMoney = 0;
    }

    updatedBooking.payableAmount = recalculated.trip_cost;

    // Dispatch update - Firebase Cloud Functions will handle wallet adjustments
    dispatch(updateBooking(updatedBooking));

    // Keep the driver's weeklyTripRevenue accumulator in sync with the new
    // fare. When this trip completed, the Cloud Function (processWeeklyGoalCredit)
    // added its gross trip_cost to weeklyTripRevenue — the value the in-app
    // Weekly Goal bonus bar (WeeklyGoalBar) credits revenue-mode tier bonuses
    // off, and the same figure finance reconciles against. If we change the
    // fare here without shifting that accumulator by the same delta, the
    // driver's bonus progress and our books drift apart.
    const driverUid = data.driver;
    const oldTripCost = parseFloat(data.trip_cost) || 0;
    const revenueDelta = parseFloat((recalculated.trip_cost - oldTripCost).toFixed(settings.decimal));
    let weeklyRevenueSynced = false;

    // Only touch the accumulator when this booking actually contributed to
    // it (weeklyGoalCounted is the Cloud Function's signal that the gross
    // fare was added). If the goal was disabled at completion time, the
    // revenue was never accumulated and there is nothing to correct.
    if (driverUid && data.weeklyGoalCounted && revenueDelta !== 0) {
      const bookingTs = parseBookingTimestamp(data.bookingDate || data.tripdate);
      const bookingWeekStart = bookingTs !== null ? getAddisWeekStartMs(bookingTs) : null;
      try {
        const res = await runTransaction(firebase.singleUserRef(driverUid), (user) => {
          if (!user) return user; // not cached yet → let RTDB refetch & retry
          // Only shift the live total if the trip still belongs to the week
          // the driver record is currently tracking. Mirrors the stale-week
          // rollover guard in processWeeklyGoalCredit so adjusting a trip from
          // an already-closed (reset) week never corrupts the current week's
          // running total. When the trip's week can't be resolved we fall
          // through and adjust — an admin is almost always correcting a
          // recent trip in the current week.
          if (bookingWeekStart !== null && Number(user.weekStartTs) !== bookingWeekStart) {
            return; // abort: different week, leave weeklyTripRevenue untouched
          }
          user.weeklyTripRevenue = Math.max(0, (Number(user.weeklyTripRevenue) || 0) + revenueDelta);
          return user;
        });
        weeklyRevenueSynced = res.committed;
      } catch (err) {
        console.error('[BookingDetails] Failed to sync driver weeklyTripRevenue after cost adjustment:', err);
      }
    }

    setSaving(false);
    setEditDialogOpen(false);
    const saved = t('booking_page_fare_saved', {
      amount: formatMoney(recalculated.trip_cost, settings),
      defaultValue: 'Fare changed to {{amount}}. Wallet entries are adjusted automatically.',
    });
    showToast.success(
      weeklyRevenueSynced
        ? `${saved} ${t('booking_page_weekly_synced', { defaultValue: "The driver's weekly bonus progress was updated too." })}`
        : saved
    );
  };

  // Function to open edit dialog
  const handleEditTripCost = () => {
    setNewTripCost(data.trip_cost?.toString() || '');
    setEditDialogOpen(true);
  };

  // M15: the receipt prints through the page (no popup window), with the
  // Ethiopic fonts loaded so Amharic names print.
  const handleReceipt = () => {
    const html = generateReceiptHtml(data, settings, t, isRTL).replace(
      '</head>',
      `<style>body{font-family:${FONT_STACK};}</style></head>`
    );
    printDocument({ html, title: data.reference ? `receipt_${data.reference}` : 'receipt' });
  };

  const openGoogleMaps = () => {
    const origin = encodeURIComponent(data?.pickupAddress);
    const destination = encodeURIComponent(data?.dropAddress);
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    window.open(googleMapsUrl, '_blank', 'noopener');
  };

  // Who booked it: the staff member on the booking (named from the staff list
  // that is already loaded, no read); otherwise the driver for street,
  // meter and offline trips, and the customer for app bookings.
  const staffUid = data?.callcenteragent || data?.fleetadmin || data?.callcentersupervisor || null;
  const bookedType = data && data.id ? getBookingType(data) : 'app';
  const driverBooked = ['street_pickup', 'donkey_meter', 'offline'].indexOf(bookedType) >= 0;
  const agentUid = staffUid || (driverBooked ? data?.driver : data?.customer) || data?.driver || data?.customer;
  const agentKind = staffUid ? 'staff' : agentUid && agentUid === data?.driver ? 'driver' : 'customer';
  const agentName = useMemo(() => {
    if (!agentUid) return null;
    if (agentKind === 'driver') return data?.driver_name || null;
    if (agentKind === 'customer') return data?.customer_name || null;
    if (!Array.isArray(staticusers)) return null;
    const u = staticusers.find((x) => x.id === agentUid);
    if (!u) return null;
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return name || null;
  }, [agentUid, agentKind, staticusers, data?.driver_name, data?.customer_name]);
  const agentIdLabel = agentKind === 'staff'
    ? t('booking_page_booked_by_id', { defaultValue: 'Staff ID' })
    : agentKind === 'driver'
      ? t('booking_page_driver_id', { defaultValue: 'Driver ID' })
      : t('booking_page_customer_id', { defaultValue: 'Customer ID' });

  const loaded = !!(data && data.id);
  const critical = settings && settings.AllowCriticalEditsAdmin;
  const hidden = t('hidden_demo', { defaultValue: 'Hidden' });
  const money = (v) => formatMoney(v, settings);
  const unit = settings.convert_to_mile ? t('mile') : t('km');
  const customerBidView = role === 'customer' && ['NEW', 'PAYMENT_PENDING'].indexOf(data?.status) !== -1;
  const fare = fareInfoOf(data);
  const fareAmount = customerBidView ? calmCaps(t('bid', { defaultValue: 'Bid' })) : money(fare.amount);
  const analysis = loaded ? analyseFare(data) : null;
  const canAdjust = (role === 'admin' || role === 'finance' || role === 'callcentersupervisor') && data?.status === 'COMPLETE';
  const canReceipt = data?.status === 'COMPLETE' || data?.status === 'PAID';
  const showMap = settings?.portalLiveTracking === true && role !== 'driver' && data?.id && data?.driver && LIVE_TRACKING_STATUSES.includes(data?.status);
  // The live map opens by itself, as on the approved board (owner, 28 Oct);
  // "Hide map" is remembered for this person's session.
  const [mapOpen, setMapOpen] = usePageState('booking_details', 'map', true);
  // The board: the open map two columns wide with the timeline beside it,
  // then Trip, Payment and Customer and driver in one row. Map hidden (or no
  // live trip): a slim full-width bar, the three cards right under it, the
  // timeline after them (lg) - no empty space beside a slim bar.
  const mapBeside = Boolean(showMap && mapOpen);
  const typeKey = loaded ? getBookingType(data) : 'app';
  const TypeIcon = TYPE_ICON[typeKey] || PhoneIphoneOutlinedIcon;
  const sharedTrip = Array.isArray(data?.passengers) && data.passengers.length > 0;

  // Back to whatever opened this booking (Bookings, a driver's or customer's
  // trips, incentives...), whose filters are kept; else to Bookings.
  const goBack = () => goBackOr(navigate, '/bookings', { state: { pageNo: state?.pageNo } });

  // ── Fare block in the summary (owner rule: Fixed price / Estimate / Fare) ─
  const renderFareBlock = () => {
    if (fare.kind === 'fixed' && !customerBidView) {
      return (
        <Box
          role="group"
          aria-label={`${t('fixed_price', { defaultValue: 'Fixed price' })} ${fareAmount}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: '10px', bgcolor: c.INFO_TEXT, color: c.WHITE }}
        >
          <LockRoundedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
          <Box>
            <Box sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>{t('fixed_price', { defaultValue: 'Fixed price' })}</Box>
            <Box sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fareAmount}</Box>
          </Box>
        </Box>
      );
    }
    if (fare.kind === 'estimate' && !customerBidView) {
      return (
        <Box
          role="group"
          aria-label={`${t('estimated_fare', { defaultValue: 'Estimate' })} ${fareAmount}`}
          sx={{ px: 2, py: 0.75, borderRadius: '10px', bgcolor: c.ESTIMATE_TINT, border: '1.5px dashed #D97706' }}
        >
          <Box sx={{ fontSize: 12.5, fontWeight: 600, color: c.AMBER_TEXT, lineHeight: 1.35 }}>{`≈ ${t('estimated_fare', { defaultValue: 'Estimate' })}`}</Box>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: c.INK, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{`~${fareAmount}`}</Box>
        </Box>
      );
    }
    return (
      <Box role="group" aria-label={`${t('fare', { defaultValue: 'Fare' })} ${fareAmount}`} sx={{ px: 2, py: 0.75, borderRadius: '10px', bgcolor: c.SURFACE_ALT }}>
        <Box sx={{ fontSize: 12.5, fontWeight: 600, color: c.INK_2, lineHeight: 1.35 }}>{t('fare', { defaultValue: 'Fare' })}</Box>
        <Box sx={{ fontSize: 22, fontWeight: 700, color: c.INK, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fareAmount}</Box>
      </Box>
    );
  };

  const copyButton = (value, what) => <CopyButton value={value} what={what} />;

  const placeBlock = (icon, hue, label, address) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: '1 1 220px' }}>
      <Box aria-hidden="true" sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: hue.bg, color: hue.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, color: c.TEXT_3, lineHeight: 1.35 }}>{label}</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: c.INK, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{address || '—'}</Typography>
      </Box>
    </Box>
  );

  const contactText = (value) => (value && String(value).trim() ? (critical ? formatPhoneDisplay(value) : hidden) : '');

  // A copy button for a phone number shown on the page: it copies the number
  // without spaces (+251922334455), ready for MicroSIP. None for a number
  // hidden in demo mode (shown = false).
  const phoneCopy = (value, what, shown = critical) =>
    shown && value && String(value).trim() ? <CopyButton value={value} phone what={what} sx={{ my: -0.5 }} /> : null;
  const customerPhoneWhat = t('customer_contact', { defaultValue: 'Customer phone' });
  const driverPhoneWhat = t('driver_contact', { defaultValue: 'Driver phone' });
  // A number with its copy button, for the label / value rows.
  const phoneValue = (value, what, shown = critical) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{shown ? formatPhoneDisplay(value) : hidden}</Box>
      {phoneCopy(value, what, shown)}
    </Box>
  );
  // "· +251922334455" and its copy button for the summary lines, kept
  // together when the line wraps (a phone-width screen).
  const summaryPhone = (value, what) =>
    contactText(value) ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>
        {`· ${contactText(value)}`}
        {phoneCopy(value, what)}
      </Box>
    ) : null;

  // ── Trip timeline ──────────────────────────────────────────────────────
  const timeline = (() => {
    if (!loaded) return [];
    const st = data.status;
    const rank = { NEW: 0, PAYMENT_PENDING: 0, PENDING_DRIVER_APPROVAL: 0, ACCEPTED: 1, ARRIVED: 2, STARTED: 3, REACHED: 4, PENDING: 4, PAID: 5, COMPLETE: 5 };
    const cancelled = st === 'CANCELLED';
    const r = rank[st] !== undefined ? rank[st] : 0;
    const arriveMs = toMs(data.driver_arrive_time);
    const startMs = toMs(data.startTime);
    const endMs = toMs(data.endTime);
    const done = {
      accepted: r >= 1 || (cancelled && !!data.driver),
      arrived: r >= 2 || (cancelled && arriveMs !== null),
      started: r >= 3 || (cancelled && startMs !== null),
      dropoff: r >= 4,
      paid: r >= 5,
    };
    const notSaved = t('booking_page_time_not_saved', { defaultValue: 'time not saved' });
    const bookedSub = [
      getBookingTypeLabel(data, t),
      agentName,
      data.bookLater && data.tripdate
        ? t('booking_page_booked_for', { time: `${addisDayLabel(data.tripdate)}, ${formatAddisTime(data.tripdate)}`, defaultValue: 'for {{time}}' })
        : null,
    ].filter(Boolean).join(' · ');
    const startedAgo = st === 'STARTED' && startMs !== null
      ? t('booking_page_minutes_ago', { count: Math.max(0, Math.round((now - startMs) / 60000)), defaultValue: '{{count}} min ago' })
      : null;
    const steps = [
      {
        key: 'booked',
        title: t('booking_page_step_booked', { defaultValue: 'Booked' }),
        sub: bookedSub,
        time: formatAddisTime(data.bookedAt || data.bookingDate) || '—',
        done: true,
      },
      {
        key: 'accepted',
        title: t('booking_page_step_accepted', { defaultValue: 'Driver accepted' }),
        sub: done.accepted ? [data.driver_name, notSaved].filter(Boolean).join(' · ') : '',
        time: done.accepted ? '—' : null,
        done: done.accepted,
      },
      {
        key: 'arrived',
        title: t('booking_page_step_arrived', { defaultValue: 'Driver arrived' }),
        sub: data.pickupAddress || '',
        time: done.arrived ? (arriveMs !== null ? formatAddisTime(arriveMs) : '—') : null,
        done: done.arrived,
      },
      {
        key: 'started',
        title: t('booking_page_step_started', { defaultValue: 'Trip started' }),
        sub: done.started
          ? [data.otp ? t('booking_page_start_code_checked', { defaultValue: 'Start code checked' }) : null, startedAgo].filter(Boolean).join(' · ')
          : '',
        time: done.started ? tripClock(data.startTime, data.trip_start_time) || '—' : null,
        done: done.started,
      },
      {
        key: 'dropoff',
        title: t('booking_page_step_dropoff', { defaultValue: 'Drop-off' }),
        sub: data.dropAddress || '',
        time: done.dropoff ? tripClock(data.endTime, data.trip_end_time) || '—' : endMs !== null && !cancelled ? formatAddisTime(endMs) : null,
        done: done.dropoff,
      },
      {
        key: 'paid',
        title: t('booking_page_step_paid', { defaultValue: 'Paid' }),
        sub: data.payment_mode ? calmCaps(t(data.payment_mode, { defaultValue: data.payment_mode })) : '',
        time: done.paid ? '—' : null,
        done: done.paid,
      },
    ];
    if (cancelled) {
      const kept = steps.filter((s) => s.done);
      kept.push({
        key: 'cancelled',
        title: calmCaps(t('CANCELLED', { defaultValue: 'Cancelled' })),
        sub: data.reason ? t(getLangKey(data.reason)) : '',
        time: '—',
        done: true,
        cancelled: true,
      });
      return kept;
    }
    // The step the trip is on now gets the ring.
    if (live) {
      const lastDone = steps.map((s) => s.done).lastIndexOf(true);
      if (lastDone >= 0) steps[lastDone].current = true;
    }
    return steps;
  })();

  const renderTimeline = () => (
    <Paper
      component="section"
      aria-label={t('booking_page_timeline', { defaultValue: 'Trip timeline' })}
      // lg: beside the open map, as tall as it (the board); otherwise after
      // Trip, Payment and Customer and driver.
      sx={{ p: 2, minWidth: 0, ...(mapBeside ? { alignSelf: { lg: 'stretch' } } : { order: { lg: 1 } }) }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK }}>
          {t('booking_page_timeline', { defaultValue: 'Trip timeline' })}
        </Typography>
        <Typography sx={{ fontSize: 13, color: c.TEXT_3 }}>
          {[addisDayLabel(data.bookedAt || data.bookingDate), t('booking_page_addis_time', { defaultValue: 'Addis Ababa time' })].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {timeline.map((step, i) => {
          const last = i === timeline.length - 1;
          const color = step.cancelled ? c.DANGER_TEXT : step.current ? c.INFO_TEXT : step.done ? c.SUCCESS : c.OUTLINE_STRONG;
          return (
            <Box component="li" key={step.key} sx={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto', columnGap: 1.25, position: 'relative', pb: last ? 0 : 1.5 }}>
              {!last ? (
                <Box aria-hidden="true" sx={{ position: 'absolute', left: 13, top: 26, bottom: 0, width: 2, bgcolor: step.done && timeline[i + 1].done ? c.SUCCESS : c.OUTLINE }} />
              ) : null}
              <Box
                aria-hidden="true"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: step.done && !step.current ? color : c.WHITE,
                  border: `2px solid ${color}`,
                  color: c.WHITE,
                  zIndex: 1,
                }}
              >
                {step.cancelled ? <CloseRoundedIcon sx={{ fontSize: 16 }} /> : step.current ? (
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.INFO_TEXT }} />
                ) : step.done ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : null}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.4, color: step.current ? c.INFO_TEXT : step.cancelled ? c.DANGER_TEXT : step.done ? c.INK : c.INK_2 }}>
                  {step.title}
                </Typography>
                {step.sub ? (
                  <Typography sx={{ fontSize: 13, color: c.TEXT_3, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{step.sub}</Typography>
                ) : null}
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: step.done ? 600 : 500, color: step.done ? c.INK : c.TEXT_3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {step.time !== null && step.time !== undefined ? step.time : t('booking_page_not_yet', { defaultValue: 'Not yet' })}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );

  // ── Trip card ──────────────────────────────────────────────────────────
  const totalTimeText = (() => {
    const secs = parseFloat(data?.total_trip_time);
    if (!Number.isFinite(secs) || secs <= 0) return '';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.round(secs % 60);
    const minWord = t('min', { defaultValue: 'min' });
    if (h > 0) return `${h} ${t('hour', { defaultValue: 'hour' })} ${m} ${minWord}`;
    if (m > 0) return `${m} ${minWord}${s ? ` ${s} ${t('sec', { defaultValue: 'sec' })}` : ''}`;
    return `${s} ${t('sec', { defaultValue: 'sec' })}`;
  })();

  const tripRows = loaded ? [
    {
      key: 'booking_id',
      label: t('booking_id', { defaultValue: 'Booking ID' }),
      value: data.id,
      render: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 }}>{data.id}</Box>
          {copyButton(data.id, t('booking_id', { defaultValue: 'Booking ID' }))}
        </Box>
      ),
    },
    { key: 'trip_date', label: t('booking_page_trip_date', { defaultValue: 'Trip date' }), value: data.tripdate ? formatAddisDateTime(data.tripdate, { weekday: true }) : '' },
    { key: 'pickup_note', label: t('booking_page_pickup_note', { defaultValue: 'Pickup note' }), value: data.pickUpInstructions },
    {
      key: 'booked_by',
      label: t('booking_page_booked_by', { defaultValue: 'Booked by' }),
      value: agentUid,
      render: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Box component="span">{agentName || agentUid}</Box>
          {copyButton(agentUid, agentIdLabel)}
          {agentName ? <Box component="span" sx={{ width: '100%', fontSize: 12.5, color: c.TEXT_3, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{agentUid}</Box> : null}
        </Box>
      ),
    },
    { key: 'street_pickup', label: calmCaps(t('street_pickup', { defaultValue: 'Street pickup' })), value: data.streetPickup ? t('yes', { defaultValue: 'Yes' }) : t('no', { defaultValue: 'No' }) },
    {
      key: 'start_code',
      label: t('booking_page_start_code', { defaultValue: 'Start code' }),
      value: data.otp ? t('booking_page_required', { defaultValue: 'Required' }) : t('booking_page_not_required', { defaultValue: 'Not required' }),
    },
    { key: 'distance_web', label: t('distance_web', { defaultValue: 'Distance' }), value: data.distance ? `${data.distance} ${unit}` : '' },
    { key: 'total_time', label: calmCaps(t('total_time', { defaultValue: 'Total time' })), value: totalTimeText },
    {
      key: 'meter_waiting_time',
      label: t('meter_waiting_time', { defaultValue: 'Waiting time' }),
      value: data.meter_wait_seconds != null && data.meter_wait_seconds > 0 ? `${Math.round(data.meter_wait_seconds / 60)} ${t('min', { defaultValue: 'min' })}` : '',
    },
    {
      key: 'parcel_type_web',
      label: t('parcel_type_web', { defaultValue: 'Parcel type' }),
      value: data.parcelTypeSelected ? `${t(getLangKey(data.parcelTypeSelected.description))} (${data.parcelTypeSelected.amount})` : '',
    },
    {
      key: 'parcel_option',
      label: t('parcel_option', { defaultValue: 'Parcel option' }),
      value: data.optionSelected ? `${t(getLangKey(data.optionSelected.description))} (${data.optionSelected.amount})` : '',
    },
    { key: 'deliveryPerson', label: t('deliveryPerson', { defaultValue: 'Receiving person' }), value: data.deliveryPerson },
    {
      key: 'deliveryPersonPhone',
      label: t('deliveryPersonPhone', { defaultValue: 'Receiving person\'s phone' }),
      value: data.deliveryPersonPhone ? (critical ? data.deliveryPersonPhone : hidden) : '',
      render: data.deliveryPersonPhone ? phoneValue(data.deliveryPersonPhone, t('deliveryPersonPhone', { defaultValue: 'Receiving person\'s phone' })) : null,
    },
    { key: 'tripInstructions', label: calmCaps(t('tripInstructions', { defaultValue: 'Trip instructions' })), value: data.tripInstructions },
    { key: 'deliveryInstructions', label: calmCaps(t('deliveryInstructions', { defaultValue: 'Booking instructions' })), value: data.deliveryInstructions },
    { key: 'otherPerson', label: t('otherPerson', { defaultValue: 'Other person' }), value: data.otherPerson },
    {
      key: 'otherPersonPhone',
      label: t('otherPersonPhone', { defaultValue: 'Other person\'s phone' }),
      value: data.otherPersonPhone,
      // Shown in every mode (as before), so it is copied in every mode too.
      render: data.otherPersonPhone ? phoneValue(data.otherPersonPhone, t('otherPersonPhone', { defaultValue: 'Other person\'s phone' })) : null,
    },
    { key: 'cancellation_reason', label: calmCaps(t('cancellation_reason', { defaultValue: 'Cancellation reason' })), value: data.reason ? t(getLangKey(data.reason)) : '' },
    { key: 'feedback', label: t('feedback', { defaultValue: 'Feedback' }), value: data.feedback },
    {
      key: 'you_rated_text',
      label: calmCaps(t('you_rated_text', { defaultValue: 'Driver rating' })),
      value: data.rating ? String(data.rating) : '',
      render: <Rating name="booking-rating" value={Number(data.rating) || 0} precision={0.5} readOnly size="small" />,
    },
    {
      key: 'take_pickup_image_web',
      label: t('take_pickup_image_web', { defaultValue: 'Pickup photo' }),
      value: data.pickup_image,
      render: data.pickup_image ? <Box component="img" src={data.pickup_image} alt={t('take_pickup_image_web', { defaultValue: 'Pickup photo' })} sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: '8px' }} /> : null,
    },
    {
      key: 'take_deliver_image_web',
      label: t('take_deliver_image_web', { defaultValue: 'Delivery photo' }),
      value: data.deliver_image,
      render: data.deliver_image ? <Box component="img" src={data.deliver_image} alt={t('take_deliver_image_web', { defaultValue: 'Delivery photo' })} sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: '8px' }} /> : null,
    },
  ] : [];

  // ── Payment card ───────────────────────────────────────────────────────
  const offer = parseFloat(data?.offerFare) || 0;
  const estimateNum = parseFloat(data?.estimate) || 0;
  const changedByCustomer = (() => {
    if (!(offer > 0)) return t('booking_page_not_changed', { defaultValue: 'No' });
    const diff = offer - estimateNum;
    const sign = diff > 0 ? '+' : diff < 0 ? '−' : '';
    const pct = estimateNum > 0 ? Math.round((Math.abs(diff) / estimateNum) * 1000) / 10 : null;
    return `${sign}${money(Math.abs(diff))}${pct !== null ? ` (${sign}${pct}%)` : ''}`;
  })();
  const hideSplit = customerBidView;
  // Who adjusted the fare (a staff name) stays with the roles that can adjust
  // it, as before: admin, finance and call-centre supervisor, on a completed
  // trip. Customers, drivers and other staff never saw it.
  const adjustedNote = canAdjust && data?.manual_cost_adjustment
    ? t('booking_page_adjusted', {
        date: formatAddisDateTime(data.manual_cost_adjustment_date),
        name: data.manual_cost_adjustment_by_name || '',
        defaultValue: 'Adjusted {{date}} by {{name}}',
      })
    : '';
  const analysisText = analysis ? analysisLines(analysis, t, settings) : [];
  const showAnalysis = !!analysis && (analysis.marks.length > 0 || analysis.score >= 30);

  const paymentRows = loaded ? [
    {
      key: 'fare',
      label: t('fare', { defaultValue: 'Fare' }),
      value: fareAmount,
      render: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {fare.kind === 'estimate' && !customerBidView ? `~${fareAmount}` : fareAmount}
            </Box>
            {!customerBidView ? <FareTag kind={fare.kind} /> : null}
          </Box>
          {adjustedNote ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, color: c.INFO_TEXT, fontWeight: 600 }}>
              <EditOutlinedIcon sx={{ fontSize: 15 }} aria-hidden="true" />
              {adjustedNote}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      key: 'anomaly',
      label: t('bookings_legend_anomaly', { defaultValue: 'Anomaly' }),
      value: showAnalysis ? 'yes' : '',
      alignTop: true,
      render: showAnalysis ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {analysis.marks.length ? (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {analysis.marks.map((m) => (
                <AnomalyMark key={m.kind} mark={m} critical={analysis.score >= 70} label={anomalyMarkText(m, t)} />
              ))}
            </Box>
          ) : null}
          {analysisText.map((line) => (
            <Box key={line} sx={{ fontSize: 12.5, color: c.INK_2 }}>{line}</Box>
          ))}
        </Box>
      ) : null,
    },
    { key: 'estimate', label: t('system_estimate', { defaultValue: 'System estimate' }), value: data.estimate ? money(data.estimate) : '' },
    { key: 'changed_by_customer', label: t('booking_page_changed_by_customer', { defaultValue: 'Changed by customer' }), value: changedByCustomer },
    { key: 'payment_mode_web', label: calmCaps(t('payment_mode_web', { defaultValue: 'Payment mode' })), value: data.payment_mode ? calmCaps(t(data.payment_mode, { defaultValue: data.payment_mode })) : '' },
    { key: 'payment_gateway', label: calmCaps(t('payment_gateway', { defaultValue: 'Payment gateway' })), value: data.gateway },
    { key: 'Customer_paid', label: t('Customer_paid', { defaultValue: 'Customer paid' }), value: data.customer_paid ? money(data.customer_paid) : '' },
    { key: 'discount_ammount', label: calmCaps(t('discount_ammount', { defaultValue: 'Discount' })), value: money(data.discount || 0) },
    { key: 'convenience_fee', label: calmCaps(t('convenience_fee', { defaultValue: 'Convenience fee' })), value: hideSplit ? '--' : money(data.convenience_fees) },
    { key: 'driver_share', label: calmCaps(t('driver_share', { defaultValue: 'Driver share' })), value: hideSplit ? '--' : money(data.driver_share) },
    {
      key: 'fleet_admin_comission',
      label: calmCaps(t('fleet_admin_comission', { defaultValue: 'Fleet admin commission' })),
      value: hideSplit ? '--' : data.fleetCommission && parseFloat(data.fleetCommission) > 0 ? money(data.fleetCommission) : '',
    },
    { key: 'tipamount', label: calmCaps(t('tipamount', { defaultValue: 'Tip' })), value: data.tipamount ? money(data.tipamount) : '' },
    {
      key: 'cancellationFee',
      label: calmCaps(t('cancellationFee', { defaultValue: 'Cancellation fee' })),
      value: parseFloat(data.cancellationFee) > 0 || data.status === 'CANCELLED' ? money(parseFloat(data.cancellationFee) || 0) : '',
    },
    { key: 'cash_payment_amount', label: t('cash_payment_amount', { defaultValue: 'Paid in cash' }), value: data.cashPaymentAmount ? money(data.cashPaymentAmount) : '' },
    { key: 'card_payment_amount', label: t('card_payment_amount', { defaultValue: 'Paid by transfer' }), value: data.cardPaymentAmount ? money(data.cardPaymentAmount) : '' },
    { key: 'wallet_payment_amount', label: t('wallet_payment_amount', { defaultValue: 'Paid from wallet' }), value: data.usedWalletMoney ? money(data.usedWalletMoney) : '' },
    { key: 'payable_ammount', label: calmCaps(t('payable_ammount', { defaultValue: 'Payable amount' })), value: data.payableAmount ? money(data.payableAmount) : '' },
  ] : [];

  // ── Customer and driver card ───────────────────────────────────────────
  const personValue = (name, image, link, phoneText, phoneCopyButton) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <Avatar src={image || undefined} alt="" sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: c.SURFACE_ALT, color: c.INK_2 }}>
        {initialsOf(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        {link}
        {phoneText ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: 12.5, color: c.TEXT_3 }}>
            <Box component="span">{phoneText}</Box>
            {phoneCopyButton || null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  const peopleRows = loaded ? [
    ...(!sharedTrip ? [
      {
        key: 'customer',
        label: t('customer', { defaultValue: 'Customer' }),
        value: data.customer_name || data.customer,
        render: personValue(
          data.customer_name,
          data.customer_image,
          data.customer_name ? (
            <ClickableUserLink value={data.customer_name} type="customer" searchField="name" userId={data.customer} showIcon={false} style={{ color: c.ACCENT_TEXT, fontWeight: 600, padding: 0, textDecoration: 'underline' }} />
          ) : <Box component="span">-</Box>,
          contactText(data.customer_contact),
          phoneCopy(data.customer_contact, customerPhoneWhat)
        ),
      },
      { key: 'customer_email', label: calmCaps(t('customer_email', { defaultValue: 'Customer email' })), value: data.customer_email ? (critical ? data.customer_email : hidden) : '' },
      {
        key: 'customer_id',
        label: t('booking_page_customer_id', { defaultValue: 'Customer ID' }),
        value: data.customer,
        render: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 }}>{data.customer}</Box>
            {copyButton(data.customer, t('booking_page_customer_id', { defaultValue: 'Customer ID' }))}
          </Box>
        ),
      },
    ] : []),
    {
      key: 'driver',
      label: t('driver', { defaultValue: 'Driver' }),
      value: data.driver_name || data.driver,
      render: personValue(
        data.driver_name,
        data.driver_image,
        data.driver_name ? (
          <ClickableUserLink value={data.driver_name} type="driver" searchField="name" userId={data.driver} showIcon={false} style={{ color: c.ACCENT_TEXT, fontWeight: 600, padding: 0, textDecoration: 'underline' }} />
        ) : <Box component="span">-</Box>,
        contactText(data.driver_contact),
        phoneCopy(data.driver_contact, driverPhoneWhat)
      ),
    },
    { key: 'driver_email', label: calmCaps(t('driver_email', { defaultValue: 'Driver email' })), value: data.driver_email ? (critical ? data.driver_email : hidden) : '' },
    {
      key: 'driver_id',
      label: t('booking_page_driver_id', { defaultValue: 'Driver ID' }),
      value: data.driver,
      render: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 }}>{data.driver}</Box>
          {copyButton(data.driver, t('booking_page_driver_id', { defaultValue: 'Driver ID' }))}
        </Box>
      ),
    },
    {
      key: 'car',
      label: t('booking_page_car', { defaultValue: 'Car' }),
      value: [data.carType ? t(getLangKey(data.carType)) : '', data.vehicle_number || ''].filter(Boolean).join(' · '),
    },
    { key: 'fleetadmins', label: calmCaps(t('fleetadmins', { defaultValue: 'Fleet admin' })), value: data.fleetadmin && role === 'admin' ? data.fleetadmin : '' },
    { key: 'device_id', label: calmCaps(t('device_id', { defaultValue: 'Device ID' })), value: data.driverDeviceId || '' },
  ] : [];

  // ── Distance tracking card (after trip end) ────────────────────────────
  const hasTracking = !!(data?.distanceCalculationMethod || data?.gpsTrackedDistance !== undefined || data?.trackingPointsCount !== undefined);
  // lg: after the timeline when it is not beside the map (see mapBeside).
  const afterTimeline = mapBeside ? undefined : { order: { lg: 2 } };
  const methodInfo = (() => {
    const method = data?.distanceCalculationMethod;
    const M = {
      gps: ['success', t('booking_page_method_gps', { defaultValue: 'GPS tracked' }), t('booking_page_method_gps_note', { defaultValue: 'Worked out from the GPS points recorded during the trip.' })],
      estimate: ['warning', t('booking_page_method_estimate', { defaultValue: 'Estimate used' }), t('booking_page_method_estimate_note', { defaultValue: 'GPS tracking was not enough, so the planned distance was used.' })],
      google: ['info', t('booking_page_method_google', { defaultValue: 'Google Maps' }), t('booking_page_method_google_note', { defaultValue: 'Worked out with Google Maps.' })],
      METER_GPS: ['success', t('booking_page_method_meter_gps', { defaultValue: 'GPS tracked (meter)' }), t('booking_page_method_meter_gps_note', { defaultValue: 'Measured by the meter from the GPS points recorded during the trip.' })],
      METER_HYBRID: ['warning', t('booking_page_method_meter_hybrid', { defaultValue: 'GPS and estimate' }), t('booking_page_method_meter_hybrid_note', { defaultValue: 'Meter GPS covered part of the trip; the gaps were filled at the fallback speed (charged at most the per-minute tariff).' })],
      METER_TIME_FALLBACK: ['warning', t('booking_page_method_meter_time', { defaultValue: 'Time fallback' }), t('booking_page_method_meter_time_note', { defaultValue: 'No GPS, so the distance was worked out from the time at the fallback speed.' })],
    };
    const hit = M[method];
    return hit ? { tone: tones[hit[0]], label: hit[1], note: hit[2] } : { tone: tones.neutral, label: method || t('booking_page_not_available', { defaultValue: 'Not available' }), note: '' };
  })();
  const gpsDistance = parseFloat(data?.gpsTrackedDistance) || 0;
  const finalDistance = parseFloat(data?.distance) || 0;
  const estimateDistance = parseFloat(data?.estimateDistance) || 0;
  const estimateDiff = estimateDistance > 0 ? ((gpsDistance - estimateDistance) / estimateDistance) * 100 : 0;
  const points = parseInt(data?.trackingPointsCount, 10) || 0;
  const pointsQuality = points >= 50
    ? { tone: tones.success, label: t('booking_page_quality_excellent', { defaultValue: 'Excellent' }) }
    : points >= 20
      ? { tone: tones.info, label: t('booking_page_quality_good', { defaultValue: 'Good' }) }
      : points >= 10
        ? { tone: tones.warning, label: t('booking_page_quality_fair', { defaultValue: 'Fair' }) }
        : { tone: tones.error, label: t('booking_page_quality_poor', { defaultValue: 'Poor' }) };
  const toneChip = (tone, label) => (
    <Chip size="small" label={label} sx={{ color: tone.fg, bgcolor: tone.bg, fontWeight: 600 }} />
  );

  const trackingRows = hasTracking ? [
    {
      key: 'distance_method',
      label: t('booking_page_calc_method', { defaultValue: 'Calculation method' }),
      value: 'x',
      alignTop: true,
      render: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          {toneChip(methodInfo.tone, methodInfo.label)}
          {methodInfo.note ? <Box sx={{ fontSize: 12.5, color: c.INK_2 }}>{methodInfo.note}</Box> : null}
        </Box>
      ),
    },
    {
      key: 'gps_tracked_distance',
      label: t('booking_page_gps_distance', { defaultValue: 'GPS tracked distance' }),
      value: data?.gpsTrackedDistance !== undefined ? 'x' : '',
      render: (
        <Box>
          <Box sx={{ fontWeight: 600 }}>{`${gpsDistance} ${unit}`}</Box>
          {estimateDistance > 0 ? (
            <Box sx={{ fontSize: 12.5, fontWeight: 600, color: estimateDiff > 20 ? c.DANGER_TEXT : estimateDiff > 10 ? c.WARNING_TEXT : c.SUCCESS_TEXT }}>
              {t('booking_page_vs_estimate', { value: `${estimateDiff > 0 ? '+' : ''}${estimateDiff.toFixed(1)}`, defaultValue: '{{value}}% vs estimate' })}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      // Donkey Meter distance mode: the portion of the billed distance that
      // was ESTIMATED (GPS gaps, charged capped at the per-minute tariff).
      // Shown so agents never read the total as all-driven.
      key: 'meter_estimated_distance',
      label: t('meter_estimated_distance', { defaultValue: 'Estimated (no GPS)' }),
      value: parseFloat(data?.meter_distance_fallback_km) > 0 ? `${data.meter_distance_fallback_km} ${unit}` : '',
    },
    {
      // Raw pre-cap estimate — only interesting when it exceeds what was
      // actually billed.
      key: 'meter_raw_estimate',
      label: t('meter_raw_estimate', { defaultValue: 'Estimate before cap (not billed)' }),
      value: parseFloat(data?.meter_distance_raw_km) > 0 && parseFloat(data?.meter_distance_raw_km) > (parseFloat(data?.distance) || 0) + 0.01
        ? `${data.meter_distance_raw_km} ${unit}`
        : '',
    },
    {
      key: 'tracking_points',
      label: t('booking_page_gps_points', { defaultValue: 'GPS points recorded' }),
      value: data?.trackingPointsCount !== undefined ? 'x' : '',
      alignTop: true,
      render: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{points}</Box>
            {toneChip(pointsQuality.tone, pointsQuality.label)}
          </Box>
          <Box sx={{ fontSize: 12.5, color: c.INK_2 }}>{t('booking_page_points_note', { defaultValue: 'More points give a more accurate distance.' })}</Box>
        </Box>
      ),
    },
    {
      key: 'distance_comparison',
      label: t('booking_page_distance_compare', { defaultValue: 'Distance comparison' }),
      value: estimateDistance || finalDistance ? 'x' : '',
      render: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {estimateDistance > 0 ? <Box sx={{ fontSize: 13 }}>{t('booking_page_dist_planned', { value: `${estimateDistance} ${unit}`, defaultValue: 'Planned: {{value}}' })}</Box> : null}
          {gpsDistance > 0 ? <Box sx={{ fontSize: 13 }}>{t('booking_page_dist_gps', { value: `${gpsDistance} ${unit}`, defaultValue: 'GPS: {{value}}' })}</Box> : null}
          {finalDistance > 0 ? <Box sx={{ fontSize: 13, fontWeight: 700 }}>{t('booking_page_dist_final', { value: `${finalDistance} ${unit}`, defaultValue: 'Billed: {{value}}' })}</Box> : null}
        </Box>
      ),
    },
  ] : [];

  const renderRows = (rows) =>
    rows
      .filter((row) => row.value !== undefined && row.value !== null && row.value !== '' && row.value !== false)
      .map((row) => (
        <InfoRow key={row.key} label={row.label} alignTop={row.alignTop}>
          {row.render !== undefined && row.render !== null ? row.render : row.value}
        </InfoRow>
      ));

  const adjustPreview = editDialogOpen && newTripCost !== '' ? computeAdjustedAmounts(newTripCost) : null;

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Button variant="text" startIcon={<ArrowBackRoundedIcon />} onClick={goBack} sx={{ color: c.ACCENT_TEXT }}>
            {calmCaps(t('go_back', { defaultValue: 'Back' }))}
          </Button>
        </Box>
        <Paper sx={{ p: 2 }}>
          <SkeletonRows rows={6} columns={3} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Summary */}
      <Paper
        component="section"
        aria-label={t('booking_page_summary', { defaultValue: 'Booking summary' })}
        sx={{ p: { xs: 1.5, md: 2 }, position: { md: 'sticky' }, top: { md: 64 }, zIndex: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Button variant="text" startIcon={<ArrowBackRoundedIcon />} onClick={goBack} sx={{ color: c.ACCENT_TEXT, ml: -1 }}>
            {calmCaps(t('go_back', { defaultValue: 'Back' }))}
          </Button>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: c.TEXT_3, lineHeight: 1.3 }}>{t('booking_page_booking', { defaultValue: 'Booking' })}</Typography>
              <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3, letterSpacing: '0.04em', color: c.INK }}>
                {data.reference || '—'}
              </Typography>
            </Box>
            {data.reference ? copyButton(data.reference, t('booking_page_reference', { defaultValue: 'Booking reference' })) : null}
          </Box>
          {data.status ? <BookingStatusChip status={data.status} size="medium" /> : null}
          <Chip
            icon={<TypeIcon />}
            label={getBookingTypeLabel(data, t)}
            sx={{ height: 30, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontWeight: 600, '& .MuiChip-icon': { color: c.INK_2, fontSize: 17 } }}
          />
          <Box sx={{ flex: '1 1 0', minWidth: 8 }} />
          {canReceipt ? (
            <Button variant="outlined" color="neutral" startIcon={<ReceiptLongOutlinedIcon />} onClick={handleReceipt}>
              {calmCaps(t('download_receipt', { defaultValue: 'Download receipt' }))}
            </Button>
          ) : null}
          {renderFareBlock()}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {placeBlock(<PlaceRoundedIcon sx={{ fontSize: 19 }} />, tones.success, t('pickup', { defaultValue: 'Pickup' }), data.pickupAddress)}
          <ArrowForwardRoundedIcon sx={{ color: c.TEXT_3, display: { xs: 'none', sm: 'block' }, transform: isRTL === 'rtl' ? 'scaleX(-1)' : 'none' }} aria-hidden="true" />
          {placeBlock(<FlagRoundedIcon sx={{ fontSize: 19 }} />, tones.error, t('booking_page_dropoff', { defaultValue: 'Drop-off' }), data.dropAddress)}
          {data.pickupAddress && data.dropAddress ? (
            <Button variant="outlined" color="neutral" startIcon={<MapOutlinedIcon />} onClick={openGoogleMaps} sx={{ ml: { md: 'auto' } }}>
              {t('google_maps', { defaultValue: 'Google Maps' })}
            </Button>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 3, rowGap: 0.75, flexWrap: 'wrap', fontSize: 14, color: c.INK_2 }}>
          {!sharedTrip ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
              <PersonOutlineRoundedIcon sx={{ fontSize: 19, color: c.TEXT_3 }} aria-hidden="true" />
              <Box component="span">{t('customer', { defaultValue: 'Customer' })}</Box>
              {data.customer_name ? (
                <ClickableUserLink value={data.customer_name} type="customer" searchField="name" userId={data.customer} showIcon={false} style={{ color: c.ACCENT_TEXT, fontWeight: 600, padding: 0, textDecoration: 'underline' }} />
              ) : <Box component="span">-</Box>}
              {summaryPhone(data.customer_contact, customerPhoneWhat)}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PeopleOutlineRoundedIcon sx={{ fontSize: 19, color: c.TEXT_3 }} aria-hidden="true" />
              <Box component="span">{t('booking_page_trip_customers', { count: data.passengers.length, defaultValue: '{{count}} customers', defaultValue_one: '{{count}} customer' })}</Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
            <DirectionsCarOutlinedIcon sx={{ fontSize: 19, color: c.TEXT_3 }} aria-hidden="true" />
            <Box component="span">{t('driver', { defaultValue: 'Driver' })}</Box>
            {data.driver_name ? (
              <ClickableUserLink value={data.driver_name} type="driver" searchField="name" userId={data.driver} showIcon={false} style={{ color: c.ACCENT_TEXT, fontWeight: 600, padding: 0, textDecoration: 'underline' }} />
            ) : <Box component="span">{t('bookings_no_driver', { defaultValue: 'No driver yet' })}</Box>}
            {summaryPhone(data.driver_contact, driverPhoneWhat)}
            {[data.carType ? t(getLangKey(data.carType)) : '', data.vehicle_number || '']
              .filter(Boolean)
              .map((part) => <Box component="span" key={part}>{`· ${part}`}</Box>)}
          </Box>
        </Box>
      </Paper>

      {/* Map slot, timeline and the cards */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          alignItems: 'start',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {/* Live driver tracking — only while a driver is actually en route or
            on the trip (ACCEPTED / ARRIVED / STARTED). Hidden for NEW,
            PENDING, PAYMENT_PENDING, REACHED, PAID, COMPLETE and CANCELLED,
            and for the driver viewing their own job. Feature-flagged by
            settings.portalLiveTracking (General Settings); the route button
            inside is flagged separately by settings.portalLiveTrackingRoute.
            See LiveTrackingMap for the data path and cost notes. */}
        {/* Open: two columns wide with the timeline beside it (the board);
            Google's own full-screen button is on the map. Hidden: one slim
            bar across the page with the cards right under it. */}
        {showMap ? (
          <Box
            sx={{
              gridColumn: mapBeside ? { md: 'span 2' } : '1 / -1',
              minWidth: 0,
              // The map card sets its own margins and shadow inline; in this
              // slot it sits flat like the cards around it.
              '& > .MuiCard-root': {
                marginTop: '0 !important',
                marginBottom: '0 !important',
                boxShadow: 'none !important',
                borderRadius: '12px !important',
                border: `1px solid ${c.OUTLINE}`,
              },
            }}
          >
            <LiveTrackingMap
              booking={data}
              firebase={firebase}
              settings={settings}
              cars={cartypes?.cars}
              t={t}
              isRTL={isRTL}
              expanded={mapOpen}
              onExpandedChange={setMapOpen}
            />
          </Box>
        ) : null}

        {renderTimeline()}

        <DetailCard icon={<RouteOutlinedIcon />} hue={tones.info} title={t('booking_page_trip', { defaultValue: 'Trip' })}>
          {renderRows(tripRows)}
        </DetailCard>

        <DetailCard
          icon={<PaymentsOutlinedIcon />}
          hue={tones.success}
          title={t('payment', { defaultValue: 'Payment' })}
          action={canAdjust && !data.manual_cost_adjustment ? (
            <Button size="small" variant="outlined" color="neutral" startIcon={<EditOutlinedIcon />} onClick={handleEditTripCost}>
              {t('booking_page_adjust_fare', { defaultValue: 'Adjust fare' })}
            </Button>
          ) : null}
        >
          {renderRows(paymentRows)}
        </DetailCard>

        <DetailCard icon={<PersonOutlineRoundedIcon />} hue={tones.accent} title={t('booking_page_people', { defaultValue: 'Customer and driver' })}>
          {renderRows(peopleRows)}
        </DetailCard>

        {sharedTrip ? (
          <DetailCard
            icon={<PeopleOutlineRoundedIcon />}
            hue={tones.amber}
            sx={afterTimeline}
            title={t('booking_page_trip_customers', { count: data.passengers.length, defaultValue: '{{count}} customers', defaultValue_one: '{{count}} customer' })}
          >
            {data.passengers.map((p, idx) => (
              <InfoRow key={p.uid || p.phone || idx} label={`${p.firstName || ''} ${p.lastName || ''}`.trim() || '-'}>
                {/* The number and its copy button stay on one line; on a narrow
                    screen the price moves under them, at the end. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', columnGap: 1 }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: c.TEXT_3, whiteSpace: 'nowrap' }}>
                    {/* Hidden in demo mode like every other number (review, 29 Oct). */}
                    {p.phone ? (critical ? formatPhoneDisplay(p.phone) : hidden) : '-'}
                    {phoneCopy(p.phone, t('phone', { defaultValue: 'Phone number' }))}
                  </Box>
                  <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', marginInlineStart: 'auto' }}>
                    {money(Number(p.price || 0).toFixed(settings.decimal))}
                  </Box>
                </Box>
              </InfoRow>
            ))}
            <InfoRow label={<Box component="span" sx={{ fontWeight: 700, color: c.INK }}>{t('share_trip_total', { defaultValue: 'Total' })}</Box>}>
              <Box sx={{ textAlign: isRTL === 'rtl' ? 'left' : 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {money(data.passengers.reduce((s, p) => s + (Number(p.price) || 0), 0).toFixed(settings.decimal))}
              </Box>
            </InfoRow>
          </DetailCard>
        ) : null}

        {/* Distance Calculation Transparency Card - Only show for completed trips with tracking data */}
        {hasTracking ? (
          <DetailCard icon={<StraightenRoundedIcon />} hue={tones.neutral} title={t('booking_page_distance_tracking', { defaultValue: 'Distance tracking' })} sx={afterTimeline}>
            {renderRows(trackingRows)}
          </DetailCard>
        ) : null}
      </Box>

      {/* Adjust fare (was "Edit Trip Cost") */}
      <Dialog
        open={editDialogOpen}
        onClose={() => (saving ? null : setEditDialogOpen(false))}
        fullScreen={phone}
        maxWidth="sm"
        fullWidth
        aria-labelledby="bd-adjust-title"
      >
        <DialogTitle id="bd-adjust-title">
          {t('booking_page_adjust_title', { reference: data.reference || '', defaultValue: 'Adjust the fare of {{reference}}' })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('booking_page_current_fare', { amount: money(data.trip_cost), defaultValue: 'Current fare: {{amount}}' })}
            </Typography>
            <InlineMessage kind="warning">
              {t('booking_page_adjust_warning', { defaultValue: 'Driver share and commission are worked out again, and the wallet entries are adjusted automatically.' })}
            </InlineMessage>
            <TextField
              autoFocus
              label={t('booking_page_new_fare', { defaultValue: 'New fare' })}
              type="text"
              fullWidth
              value={newTripCost}
              onChange={(e) => setNewTripCost(e.target.value.replace(/[^\d.]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && adjustPreview && adjustPreview.ok && !saving) handleTripCostUpdate(); }}
              inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
              InputProps={settings.symbol ? { [settings.swipe_symbol ? 'endAdornment' : 'startAdornment']: <InputAdornment position={settings.swipe_symbol ? 'end' : 'start'}>{settings.symbol}</InputAdornment> } : undefined}
              error={!!adjustPreview && !adjustPreview.ok}
              helperText={adjustPreview && !adjustPreview.ok ? adjustProblemText(adjustPreview.reason) : ' '}
            />
            {adjustPreview && adjustPreview.ok ? (
              <Box sx={{ p: 2, bgcolor: c.SURFACE_ALT, borderRadius: '10px' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1 }}>{t('booking_page_preview', { defaultValue: 'After saving' })}</Typography>
                <Box component="dl" sx={{ m: 0 }}>
                  <InfoRow label={t('fare', { defaultValue: 'Fare' })}>{money(adjustPreview.values.trip_cost)}</InfoRow>
                  <InfoRow label={calmCaps(t('driver_share', { defaultValue: 'Driver share' }))}>{money(adjustPreview.values.driver_share)}</InfoRow>
                  <InfoRow label={t('booking_page_commission', { defaultValue: 'Commission' })}>{money(adjustPreview.values.convenience_fees)}</InfoRow>
                  {adjustPreview.values.fleetCommission > 0 ? (
                    <InfoRow label={t('booking_page_fleet_commission', { defaultValue: 'Fleet commission' })}>{money(adjustPreview.values.fleetCommission)}</InfoRow>
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={() => setEditDialogOpen(false)} disabled={saving}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={handleTripCostUpdate}
            variant="contained"
            disabled={saving || !adjustPreview || !adjustPreview.ok}
          >
            {t('booking_page_save_fare', { defaultValue: 'Save fare' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BookingDetails;
